import { createAsyncThunk } from '@reduxjs/toolkit';
import { isVideo } from '../../../helper/fileHelper';
import { ONLINE_STATUS } from '../../../helper/userStatusHelper';
import changeSelectedChannelId from '../../channels/channelsThunks/changeSelectedChannelId';
import scrollToMessageId from '../../chat/chatThunks/scrollToMessageId';
import { getCurrentUserId, getCurrentUserStatus, getIsAgent } from '../../currentUser/currentUserSelectors';
import { getPlatform } from '../../init/initSelectors';
import { getMessage, getMessageAuthorName } from '../../messages/messageSelector';
import { sendMessagesOffline } from '../messageInputActions/sendMessagesOffline';
import {
  createForwardMessageModel,
  createMediaMessageModel,
  createOrderMessageModel,
  createProcessMessageModel,
  createProductMessageModel,
  createReplyMessageModel,
  createTextMessageModel,
  createTicketPartMessageModel,
  createVideoMessageModel,
} from '../messageInputHelpers/sendCurrentMessageStateHelper';
import {
  getChannelInputForwardRecipientChannelId,
  getChannelInputStagedFiles,
  getChannelInputStagedForwardMessageIds,
  getChannelInputStagedMessageSubType,
  getChannelInputStagedOrder,
  getChannelInputStagedProcess,
  getChannelInputStagedProduct,
  getChannelInputStagedReplyMessageId,
  getChannelInputText,
  getETSForwardRecipientsChannelIds,
  getEtsMessageStaged,
  getEtsTicketPartMessageStaged,
} from '../messageInputSelector';

import { channelInputReset, resetEtsMessageState } from '../messageInputSlice';

/**
 *
 * Entry point for sending messages
 *
 * Gets all the data from the state which is required for sending a message
 * creates one or more AbstractMessageModel(s) of a specific type
 * then triggers sending the message using the sendMessagesOffline action
 * finally performs various cleanup operations after sending the message
 *
 */
export const sendCurrentMessageState = createAsyncThunk(
  'messageInput/sendCurrentMessageState',
  async ({ channelId }, { dispatch, getState }) => {
    const currentState = getState();
    const platform = getPlatform(currentState);
    const text = getChannelInputText(channelId)(currentState);
    const mediaFiles = getChannelInputStagedFiles(channelId)(currentState);
    const messageSubType = getChannelInputStagedMessageSubType(channelId)(currentState);
    const stagedReplyMessageId = getChannelInputStagedReplyMessageId(channelId)(currentState);
    const stagedProduct = getChannelInputStagedProduct(channelId)(currentState);
    const stagedProcess = getChannelInputStagedProcess(channelId)(currentState);
    const stagedOrder = getChannelInputStagedOrder(channelId)(currentState);
    const stagedTicketPart = getEtsTicketPartMessageStaged(currentState);
    const etsMessage = getEtsMessageStaged(currentState);
    const stagedForwardMessageIds = getChannelInputStagedForwardMessageIds(channelId)(currentState);
    const otherRecipients = getChannelInputForwardRecipientChannelId(channelId)(currentState);
    const etsChannelsIdsRecipients = getETSForwardRecipientsChannelIds(currentState);
    const currentUserId = getCurrentUserId(currentState);

    const hasForwardMessageStaged = !!stagedForwardMessageIds.length;

    let messages = [];

    if (hasForwardMessageStaged) {
      const shouldIncludeSeparateTextMessage = stagedForwardMessageIds.length > 1;
      for (const recipient of otherRecipients) {
        for (const stagedForwardMessageId of stagedForwardMessageIds) {
          const message = getMessage(stagedForwardMessageId)(currentState);
          const authorName = getMessageAuthorName(stagedForwardMessageId)(currentState);
          const textBody = shouldIncludeSeparateTextMessage ? '' : text;
          const forwardedMessage = createForwardMessageModel({
            message,
            authorName,
            recipient,
            stagedForwardMessageId,
            text: textBody,
            currentUserId,
            platform,
          });
          messages.push(forwardedMessage);
        }
        if (shouldIncludeSeparateTextMessage && text) {
          const textMessage = createTextMessageModel({
            channelId: recipient,
            text,
            messageSubType,
            platform,
            currentUserId,
          });
          messages.push(textMessage);
        }
      }
    } else {
      if (mediaFiles.length > 0) {
        const mediaMessages = await Promise.all(
          mediaFiles.map((file) =>
            isVideo(file)
              ? createVideoMessageModel({
                  channelId,
                  file,
                  platform,
                  currentUserId,
                })
              : createMediaMessageModel({
                  channelId,
                  file,
                  platform,
                  currentUserId,
                }),
          ),
        );
        messages = [...mediaMessages];
      }

      let message;
      if (stagedReplyMessageId && text?.trim()?.length > 0) {
        message = createReplyMessageModel({
          stagedReplyMessageId,
          channelId,
          text,
          messageSubType,
          platform,
          currentUserId,
        });
      } else if (stagedProduct) {
        message = createProductMessageModel({
          stagedProduct,
          channelId,
          text,
          messageSubType,
          platform,
          currentUserId,
        });
      } else if (stagedProcess) {
        message = createProcessMessageModel({
          stagedProcess,
          channelId,
          text,
          messageSubType,
          platform,
          currentUserId,
        });
      } else if (stagedOrder) {
        message = createOrderMessageModel({
          stagedOrder,
          channelId,
          text,
          messageSubType,
          platform,
          currentUserId,
        });
      } else if (stagedTicketPart) {
        for (const channelId of etsChannelsIdsRecipients) {
          const forwardedEtsMessage = createTicketPartMessageModel({
            stagedTicketPart,
            channelId,
            text: etsMessage,
            messageSubType,
            platform,
            currentUserId,
          });
          messages.push(forwardedEtsMessage);
        }
        dispatch(
          changeSelectedChannelId({ selectedChannelId: etsChannelsIdsRecipients[etsChannelsIdsRecipients.length - 1] }),
        );
      } else if (text && text?.trim()?.length > 0) {
        message = createTextMessageModel({
          channelId,
          text,
          messageSubType,
          platform,
          currentUserId,
        });
      }

      if (message) {
        messages.push({ ...message });
      }
    }

    if (messages.length === 0) {
      return;
    }

    dispatch(channelInputReset({ channelId }));
    dispatch(resetEtsMessageState());

    await Promise.all(
      messages.map(async (message) => {
        await dispatch(sendMessagesOffline({ messages: [message] }));
      }),
    );

    const lastMessagePendingId = messages[messages.length - 1]?.clientMessageId;

    if (hasForwardMessageStaged && otherRecipients && otherRecipients[0]) {
      const localSelectedChannelId = otherRecipients[0];
      dispatch(changeSelectedChannelId({ selectedChannelId: localSelectedChannelId }));
      dispatch(scrollToMessageId({ channelId: localSelectedChannelId, messageId: lastMessagePendingId }));
    } else {
      dispatch(scrollToMessageId({ channelId, messageId: lastMessagePendingId }));
    }
  },
);
